import type { IntlShape } from 'react-intl';

/** A number for a tile or a table cell. A missing number is a dash, never a zero. */
export function formatStreamingNumber(intl: IntlShape, value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : intl.formatNumber(value);
}

/** A short number for a tile: 48,300 stays, 1,234,567 becomes 1.2M. */
export function formatCompact(intl: IntlShape, value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return Math.abs(value) >= 100_000
    ? intl.formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 })
    : intl.formatNumber(value);
}

/**
 * An amount of money. With a currency code the amount has its sign ("$412.86", "412,86 €").
 * With no code (the source did not say) it is a plain number with two decimals: the screen
 * adds a note. Never assume USD.
 */
export function formatMoney(intl: IntlShape, value: number | null | undefined, currency: string | null): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (currency !== null) {
    try {
      return intl.formatNumber(value, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
    } catch {
      // An unknown code: the number with the code after it.
      return `${intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    }
  }
  return intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A change in percent: "+12.4%". Null gives null, so the caller draws no pill. */
export function formatChangePct(intl: IntlShape, changePct: number | null | undefined): string | null {
  if (changePct === null || changePct === undefined || !Number.isFinite(changePct)) {
    return null;
  }
  return intl.formatNumber(changePct / 100, { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' });
}

/** The percent change between two numbers, or null when it cannot be stated. */
export function changePctOf(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

/** A calendar day (`YYYY-MM-DD`) as "Sep 5". The day is a plain date, so it is read in UTC. */
export function formatDay(intl: IntlShape, day: string): string {
  return intl.formatDate(new Date(`${day}T00:00:00.000Z`), { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** A month (`YYYY-MM`) as "Sep 2026". */
export function formatMonth(intl: IntlShape, month: string): string {
  return intl.formatDate(new Date(`${month}-01T00:00:00.000Z`), { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** The name of a country from its ISO 3166-1 alpha-2 code, in the UI language. */
export function countryName(intl: IntlShape, code: string): string {
  try {
    const names = new Intl.DisplayNames([intl.locale], { type: 'region' });
    return names.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}
